import {
  useGetDemoSearchLinksResultsQuery,
  useGetShareFiltersMutation,
} from '@carboncopyco/data';
import { Separator, TextLargeParagraph } from '@carboncopyco/ui';
import { capitalize } from '@carboncopyco/utils';
import { useUpdateEffect } from 'ahooks';
import { useEffect } from 'react';
import { SortDropdown } from '../../../UrlStateControls';
import { useUrlState } from '../../../utils';
import { MultiStringFilter } from '../../Controls/MultiStringFilter';
import { CollectionCard } from '../../InfoPageCards';
import type { UrlState } from '../../types';
import { useDemoPageProps } from '../DemoPageContext';
import { DemoPageTabLayout } from '../DemoPageTabLayout';
import { LinksTable } from './LinksTable';

export const LinksTab = () => {
  const { organizationId, demoId, workspaceId } = useDemoPageProps();
  const { data: linksResponse } = useGetDemoSearchLinksResultsQuery({
    organizationId,
    workspaces: [workspaceId],
    id: demoId,
  });
  const { items: links, metadata } = linksResponse || {};

  const [getShareFilters, { data: filters }] = useGetShareFiltersMutation();
  const [urlState, setUrlState] = useUrlState<UrlState>({});
  const { query } = urlState;

  useEffect(() => {
    getShareFilters({
      workspaces: [workspaceId],
      organizationId: Number(organizationId),
      entityType: 'demo',
      entityId: Number(demoId),
      query,
    });
  }, [getShareFilters, workspaceId, demoId, organizationId, query]);

  useUpdateEffect(() => {
    setUrlState({
      creators: undefined,
      companies: undefined,
      sourcePresentationIds: undefined,
    });
  }, [demoId, organizationId, query]);

  const creatorOptions =
    filters?.creators?.map((creator) => ({
      id: creator.userId,
      label: capitalize(creator.userName),
    })) || [];

  const companyOptions =
    filters?.companies?.map((company) => ({
      id: company,
      label: company,
    })) || [];

  const sourceOptions =
    filters?.sources?.map((source) => ({
      id: String(source.sourceId),
      label: source.sourceName,
    })) || [];
  return (
    <DemoPageTabLayout>
      <div className="pb-10">
        <CollectionCard
          inputPlaceholder="Search by link name"
          withSearch
          radius
          header={
            <div className="flex gap-2 py-6">
              <TextLargeParagraph
                color="text-ds-natural-700"
                label="Shared links"
              />
              {!!links?.length && (
                <TextLargeParagraph
                  color="text-ds-natural-300"
                  label={`(${metadata?.totalItems})`}
                />
              )}
            </div>
          }
        >
          <>
            <div className="mt-1" />
            <div className="flex p-4 mt-1 gap-3">
              <SortDropdown />
              <div>
                <Separator />
              </div>
              <MultiStringFilter
                capsuleWidth={192}
                placeholder="Created by"
                options={creatorOptions}
                type="creators"
              />
              <MultiStringFilter
                placeholder="Presenting to"
                capsuleWidth={192}
                options={companyOptions}
                type="companies"
              />
              <MultiStringFilter
                capsuleWidth={192}
                contentWidth={212}
                placeholder="Source"
                options={sourceOptions}
                type="sourcePresentationIds"
              />
            </div>

            <LinksTable />
          </>
        </CollectionCard>
      </div>
    </DemoPageTabLayout>
  );
};
